import { useState, useEffect, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, getDefaultRoute } from '@/auth/AuthContext';
import { authApi } from '@/api/client';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { BrandMark, SlashField } from '@/components/BrandMark';

export function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? '';
  const [mode, setMode] = useState<'signin' | 'forgot' | 'sent'>('signin');
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  useEffect(() => {
    authApi.setupStatus()
      .then((status) => {
        if (status.needsSetup) {
          navigate('/setup', { replace: true });
        }
      })
      .catch(() => {
        /* backend may not be ready */
      })
      .finally(() => setCheckingSetup(false));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      navigate(getDefaultRoute(user.role, user.permissions), { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setResetEmail(email);
    setResetError('');
    setMode('forgot');
  };

  const backToSignIn = () => {
    if (resetEmail) setEmail(resetEmail);
    setResetError('');
    setMode('signin');
  };

  const handleResetRequest = async (e: FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      await authApi.requestReset(resetEmail.trim());
      setMode('sent');
    } catch (err) {
      const status = (err as { status?: number }).status;
      setResetError(
        status === 429
          ? 'Too many reset requests from this device. Please wait a while and try again.'
          : ((err as { message?: string }).message ?? 'Could not send a reset link. Please try again.'),
      );
    } finally {
      setResetLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="auth-page">
        <div className="loading-center"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <BrandMark variant="light" size="lg" product="DispatchDash" />
        <h1 className="auth-headline">Moving in sync.</h1>
        <p className="auth-tagline">Trucking · Warehousing · Fulfilment</p>
      </div>

      {mode === 'forgot' && (
        <form className="auth-form" onSubmit={handleResetRequest}>
          <p className="kicker" style={{ color: 'var(--color-ink)' }}>Forgot password</p>
          <h2>Reset your password</h2>
          <p className="auth-help">
            Enter the email you sign in with. We'll email you a link to choose a new password.
          </p>

          {resetError && <div className="auth-error" role="alert">{resetError}</div>}

          <Input
            label="Email"
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@11eleven.ph"
          />

          <Button type="submit" variant="signal" size="lg" fullWidth loading={resetLoading}>
            Send reset link
          </Button>
          <p className="auth-alt">
            <button type="button" className="auth-link" onClick={backToSignIn}>
              Back to sign in
            </button>
          </p>
        </form>
      )}

      {mode === 'sent' && (
        <div className="auth-form" role="status">
          <p className="kicker" style={{ color: 'var(--color-ink)' }}>Check your email</p>
          <h2>Reset link sent</h2>
          <p className="auth-help">
            If that email has an account, we've sent a reset link. It expires in 1 hour.
          </p>
          <Button type="button" variant="signal" size="lg" fullWidth onClick={backToSignIn}>
            Back to sign in
          </Button>
        </div>
      )}

      {mode === 'signin' && (
      <form className="auth-form" onSubmit={handleSubmit}>
        <p className="kicker" style={{ color: 'var(--color-ink)' }}>Sign in</p>
        <h2>DispatchDash</h2>

        {notice && !error && <div className="auth-notice" role="status">{notice}</div>}
        {error && <div className="auth-error">{error}</div>}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          placeholder="you@11eleven.ph"
        />

        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        <p className="auth-forgot">
          <button type="button" className="auth-link" onClick={openForgot}>
            Forgot password?
          </button>
        </p>

        <Button type="submit" variant="signal" size="lg" fullWidth loading={loading}>
          Sign in
        </Button>
      </form>
      )}

      <div className="auth-footer">
        <SlashField />
        <span>11elevenlogistics.ph</span>
      </div>

      <style>{`
        .auth-page {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          background: var(--color-ink);
          position: relative;
        }
        .auth-hero {
          text-align: left;
          width: 100%;
          max-width: 400px;
          margin-bottom: var(--space-xl);
        }
        .auth-headline {
          font-family: var(--font-display);
          font-size: 2.25rem;
          font-weight: 700;
          color: var(--color-white);
          letter-spacing: -0.02em;
          margin: var(--space-lg) 0 var(--space-sm);
        }
        .auth-tagline {
          color: var(--color-steel);
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .auth-form {
          background: var(--color-white);
          padding: var(--space-xl);
          border-radius: var(--radius-md);
          width: 100%;
          max-width: 400px;
          box-shadow: var(--shadow-md);
        }
        .auth-form h2 {
          margin-bottom: var(--space-lg);
          color: var(--color-ink);
        }
        .auth-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .auth-notice {
          background: #E6F4EA;
          color: var(--color-ink);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .auth-help {
          color: var(--color-steel);
          font-size: 0.875rem;
          margin: calc(-1 * var(--space-sm)) 0 var(--space-lg);
        }
        .auth-forgot {
          text-align: right;
          margin: calc(-1 * var(--space-sm)) 0 var(--space-md);
          font-size: 0.875rem;
        }
        .auth-alt {
          text-align: center;
          margin-top: var(--space-md);
          font-size: 0.875rem;
        }
        .auth-link {
          background: none;
          border: none;
          padding: 0;
          color: var(--color-ink);
          text-decoration: underline;
          cursor: pointer;
          font: inherit;
        }
        .auth-footer {
          width: 100%;
          max-width: 400px;
          margin-top: var(--space-2xl);
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          color: var(--color-steel);
          font-size: 0.75rem;
          letter-spacing: 0.04em;
        }
      `}</style>
    </div>
  );
}
